import type { PermissionEnum } from '@Application/Security/Roles';
import useUserPermissions from '@UI/Providers/PermissionProvider/useUserPermissions';
import * as React from 'react';

export type PermissionGateMode = 'all' | 'any';

function useHasPermission(
	permission: PermissionEnum | readonly PermissionEnum[],
	mode: PermissionGateMode = 'all',
): boolean {
	const { hasPermission, hasAllPermissions, hasAnyPermission } = useUserPermissions();

	return React.useMemo(() => {
		// `Array.isArray` narrows against a mutable `any[]`, so it cannot exclude a `readonly` array
		// from the negated branch. `typeof` does, since `PermissionEnum` is a string enum.
		if (typeof permission === 'string') {
			return hasPermission(permission);
		}

		return mode === 'any' ? hasAnyPermission(permission) : hasAllPermissions(permission);
	}, [permission, mode, hasPermission, hasAllPermissions, hasAnyPermission]);
}

export default useHasPermission;
